import { Inject } from '@odinjs/odin';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { BloodManagementInterruptAliases as BloodAliases, FragmentNames } from '../../../../../codegen';
import BaseController from '../../../../BaseController';
import { AutocompleteAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { BloodManagementType, ReasonType } from '../blood-management-types';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { PrescriptionType, solutionMapping } from '../../../services/timeline-service';
import moment from 'moment';
import { EventType } from '../../enteral-nutrition/enteral-nutrition-type';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Value } from '@philips-internal/oswald-ui-core';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { AdminModalsService } from '../../../../../controllers/adep/services/admin-modals-service';
import { ApplicationRoute } from '../../../../../constants/routes';
import MedicationService from '../../../../../controllers/adep/services/medication-service';
import { ToastLevel } from '@philips-internal/oswald-ui-constants/lib';
import { Endpoint } from '../../../../../constants/endpoints';

@Controller({ component: BloodAliases.BLOOD_INTERRUPT, fragment: FragmentNames.BLOOD_MANAGEMENT_INTERRUPT })
export default class BloodManagementInterruptController extends BaseController {

  @Inject(BloodAliases.NOTES_TEXT_AREA)
  private notesJustificationTextAreaAPI!: TextAreaAPI;

  @Inject(BloodAliases.PARTIAL_VOLUME_INPUT)
  private partialVolumeInputAPI!: InputAPI;

  @Inject(BloodAliases.DISCARDED_VOLUME_INPUT)
  private discardedVolumeInputAPI!: InputAPI;

  @Inject(BloodAliases.INFUSED_VOLUME_INPUT)
  private infusedVolumeInputAPI!: InputAPI;

  @Inject(BloodAliases.TITLE_FIELD)
  private titleFieldAPI!: FieldAPI

  @Inject(BloodAliases.INTERRUPT_FORM)
  private interruptFormAPI!: FormAPI

  @Inject(BloodAliases.REASON_AUTOCOMPLETE)
  private reasonAutocompleteAPI!: AutocompleteAPI;

  @OnMount(BloodAliases.BLOOD_INTERRUPT)
  handleLayoutInterruptMount(): void {
    const eventType = EventType.INTERRUPTION_OF_SOLUTIONS;
    const discardedVolume = 0;
    const { bloodManagement } = this.context.get('bloodManagement') as { bloodManagement: BloodManagementType }
    this.context.set({ bloodManagement, eventType });
    this.titleFieldAPI.setData({ materialDescription: bloodManagement.materialDescription });
    this.interruptFormAPI.setData({
      ...bloodManagement,
      discardedVolume,
      infusedVolume: bloodManagement.totalVolume,
      stageVolume: bloodManagement.totalVolume
    });
  }

  @OnMount(BloodAliases.SCHEDULE_DATE_INPUT)
  handleScheduleDaeFieldMount(event: MountEvent): void {
    const { bloodManagement } = this.context.get('bloodManagement') as { bloodManagement: BloodManagementType }
    const inputApi = event.target as InputAPI;
    inputApi.setValue(moment(bloodManagement.scheduleDate).utc().format(ISO_FULL_DATE_FORMAT));
    inputApi.setReadOnly(true);
  }

  @OnBeforeCancel(BloodAliases.INTERRUPT_FORM)
  handleCancelForm(): void {
    applicationContext.popNavigationItem();
  }

  @OnMount(BloodAliases.REASON_FETCH)
  handleFetchReasonMount(event: MountEvent): void {
    const fetchReason = event.target as FetchAPI;
    const establishmentCode = foundationContext.getEstablishment()?.id;
    fetchReason.setParameters({ establishmentCode, status: PrescriptionType.BLOOD_MANAGEMENT }, { skip: true });
  }

  @OnMount(BloodAliases.DATE_INPUT)
  handleDateFieldMount(event: MountEvent): void {
    const inputApi = event.target as InputAPI;
    const currentTimeMoment = moment();
    this.context.set({ currentTime: currentTimeMoment.format(ISO_FULL_DATE_FORMAT) });
    inputApi.setValue(currentTimeMoment.utc().format(ISO_FULL_DATE_FORMAT));
  }

  @OnChange(BloodAliases.DISCARDED_VOLUME_INPUT)
  handleDiscardedInputChange(event: ChangeEvent<number>): void {
    this.handleVolumeInputChange(event, this.infusedVolumeInputAPI);
  }

  @OnChange(BloodAliases.INFUSED_VOLUME_INPUT)
  handleInfusedInputChange(event: ChangeEvent<number>): void {
    this.handleVolumeInputChange(event, this.discardedVolumeInputAPI);
  }

  @OnBeforeSubmit(BloodAliases.INTERRUPT_FORM)
  handleBeforeSubmit(): void {
    this.interruptFormAPI.validate();
    const body = this.getRequestBody();
    this.generatePresciptionChangesInterrupt(body)
  }

  private getRequestBody(): Record<string, Value> {
    const bloodManagement = this.context.get('bloodManagement')
    const { currentTime } = this.context.getBulkData() as { currentTime: string };
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const reasonSelected = this.reasonAutocompleteAPI.getValue() as unknown as ReasonType;
    const changeTypeInterrupt = 2;

    return {
      changeDate: currentTime,
      changeType: changeTypeInterrupt,
      dateTime: null,
      despisedVolumeQuantity: this.discardedVolumeInputAPI.getValue(),
      dosageType: null,
      encounterNumber: encounterId as number,
      infusedVolumeQuantity: this.infusedVolumeInputAPI.getValue(),
      justificationDescription: this.notesJustificationTextAreaAPI.getValue() as string ?? reasonSelected.reasonDescription,
      partialVolumeQuantity: this.partialVolumeInputAPI.getValue(),
      prescriptionNumber: bloodManagement.prescriptionNumber,
      reasonSequenceNumber: reasonSelected.sequenceNumber,
      solutionSequenceNumber: bloodManagement.materialSequence,
      solutionType: solutionMapping[PrescriptionType.BLOOD_MANAGEMENT],
      volumePhaseQuantity: this.infusedVolumeInputAPI.getValue() as number,
    }
  }

  private generatePresciptionChangesInterrupt(body: Record<string, Value>): void {
    HttpService.post(Endpoint.ADEP_GENERATE_CHANGE_SOLUCAO_PRESCR, {}, { body })
      .then(response => {
        const responseBody = response.body as { messageDescription: string };
        if (responseBody) {
          this.showResponseMessage(responseBody?.messageDescription);
          this.redirectToTimeline();
        }
      }).catch((error) => {
        if (error.body && error.body.errors) {
          error.body.errors.forEach((message: string) => MedicationService.showToast(MessageService.translate(message), ToastLevel.ERROR));
        }
      })
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    MedicationService.showToast(MessageService.translate('txt.emar.item-interupted-with-success'), ToastLevel.SUCCESS);
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.BLOOD_MANAGEMENT_INTERRUPT);
    AdminModalsService.redirectToTimeline(FragmentNames.BLOOD_MANAGEMENT_VIEWER);
  }

  private handleVolumeInputChange(event: ChangeEvent<number>, input: InputAPI): void {
    const ZERO_VALUE = 0;
    const actualValue = event.value as number || ZERO_VALUE;
    const oldValue = event.oldValue;
    const bloodManagement = this.context.get('bloodManagement')
    if (actualValue && (actualValue !== oldValue)) {
      const partialVolume = this.partialVolumeInputAPI.getValue() as number;
      const updatedVolume = this.calculateVolumeSolution(actualValue, partialVolume, bloodManagement?.totalVolume);
      input.setValue(updatedVolume);
    }
  }

  private calculateVolumeSolution(infusedVolume: number, partialVolume: number, stageVolume: number): number {
    const ZERO_VALUE = 0;
    if (infusedVolume !== ZERO_VALUE && stageVolume > ZERO_VALUE) {
      const result = stageVolume - partialVolume - infusedVolume;
      return result < ZERO_VALUE ? ZERO_VALUE : result;
    }

    return ZERO_VALUE;
  }
}
